import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../lib/SettingsContext";

const NAV = [
  { to: "/servizi", label: "Servizi", testid: "nav-link-servizi" },
  { to: "/prezzi", label: "Prezzi", testid: "nav-link-prezzi" },
  { to: "/amministrazioni", label: "Amministrazioni", testid: "nav-link-amministrazioni" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 border-b ${
          scrolled
            ? "bg-avorio/85 backdrop-blur-md border-bronzo/25 shadow-[0_1px_24px_rgba(32,40,37,0.06)]"
            : "bg-avorio/60 backdrop-blur-sm border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-16 md:h-20 flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigazione principale">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors duration-300 hover:text-bronzo ${
                    isActive ? "text-bronzo" : "text-ink/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            {settings.phone && (
              <a
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                data-testid="header-phone-link"
                className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-ink/80 hover:text-bronzo transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                {settings.phone}
              </a>
            )}
            <Link
              to="/contatti"
              data-testid="header-cta-intervento"
              className="hidden sm:inline-flex items-center bg-antracite text-avorio text-sm font-medium px-5 py-2.5 hover:bg-bronzo transition-colors duration-300"
            >
              Richiedi un intervento
            </Link>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setOpen(!open)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-ink"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              aria-expanded={open}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-antracite text-avorio lg:hidden flex flex-col"
          >
            <div className="h-16 shrink-0" />
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Menu mobile">
              {[{ to: "/", label: "Home", testid: "mobile-nav-home" }, ...NAV, { to: "/contatti", label: "Contatti", testid: "mobile-nav-contatti" }].map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={item.testid}
                    className={({ isActive }) =>
                      `block font-display font-light text-3xl py-2 transition-colors duration-300 ${
                        isActive ? "text-bronzo" : "text-avorio hover:text-bronzo"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-8 pb-10 space-y-4">
              <Link
                to="/contatti"
                data-testid="mobile-cta-intervento"
                className="block text-center bg-bronzo text-white font-medium px-6 py-4"
              >
                Richiedi un intervento
              </Link>
              {settings.phone && (
                <a
                  href={`tel:${settings.phone.replace(/\s/g, "")}`}
                  data-testid="mobile-phone-link"
                  className="block text-center text-avorio/70 text-sm"
                >
                  {settings.phone}
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
